import { useEffect, useMemo, useState } from 'react';
import { buildEbookToc } from '@/utils/ebook-toc';
import { useEbookLabels } from '@/utils/ebook-labels';

const EbookTableOfContents = ({ content, title, sticky = false, shown = false }) => {
  const labels = useEbookLabels();
  const resolvedTitle = title || labels.tocTitle;
  const items = useMemo(() => buildEbookToc(content), [content]);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (items.length === 0 || typeof window === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top);

        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: '-20% 0px -60% 0px',
        threshold: [0, 0.25, 0.5, 1]
      }
    );

    const elements = items.map((item) => document.getElementById(item.anchor)).filter((element) => element !== null);

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) {
    return null;
  }

  // When `sticky`, the TOC detaches into a sticky nav (desktop only) and fades in via
  // `shown`. Otherwise it sits in normal flow and scrolls away with the rest of the sidebar.
  const stickyClasses = sticky
    ? `xl:sticky xl:top-[88px] transition-[opacity,transform] duration-[350ms] ease-out ${
        shown ? 'translate-y-0 opacity-100' : '-translate-y-2.5 opacity-0'
      }`
    : '';

  return (
    <nav aria-label={resolvedTitle} className={`bg-grey-50 rounded-2xl px-6 py-5 ${stickyClasses}`}>
      <div className="text-grey-500 mb-[14px] text-xs font-bold tracking-[0.08em] uppercase">{resolvedTitle}</div>
      <ul className="flex flex-col gap-1">
        {items.map((item) => {
          const isActive = activeId === item.anchor;

          return (
            <li key={item.id}>
              <a
                href={`#${item.anchor}`}
                className={`flex gap-2.5 py-1 text-sm transition-colors ${
                  isActive ? 'text-brand-500 font-semibold' : 'text-grey-600 hover:text-grey-900'
                }`}
              >
                <span className={`shrink-0 tabular-nums ${isActive ? 'text-brand-500' : 'text-grey-400'}`}>
                  {item.number}
                </span>
                <span>{item.label}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default EbookTableOfContents;
